/**
 * CPU viewport culling + LOD assignment (PR-J).
 *
 * # Pipeline shape
 *
 *   spatial.intersect(viewportRect)  →  visible RenderableEntity list
 *   refreshLodState(prev, visible, zoom)
 *   group by bucket → { skip, point, bbox-line, full }
 *
 * Callers feed the resulting `VisibleEntityGroups` into:
 *   * `BatchedLayer.bulkLoad` — the `full` bucket.
 *   * `bbox-line` adapter (this module) — the bbox bucket gets
 *     turned into bare `RenderableEntity` line entries.
 *   * `THREE.Points` cloud — the `point` bucket gets condensed into
 *     a single point per entity.
 *   * `skip` bucket is dropped entirely.
 *
 * # Q17.A baseline
 *
 * The plan (Q17.A) earmarks rbush as the baseline visible-set
 * provider; GPU compute culling is post-PR-K work pending real-world
 * frame-time measurement. This module is the rbush path; it is the
 * sole producer of "what to render this frame" data for the renderer.
 *
 * # Why a separate module
 *
 * Keeps Scene3DStage focused on the THREE.* mesh lifecycle and lets
 * unit tests cover the visibility logic without a GL context.
 */

import {
  refreshLodState,
  type LodEntityState,
} from "./lodTransitions";
import type { LodBucket } from "./lodPolicy";
import type {
  RenderableEntity,
  SpatialIndex,
  ViewportRect,
  Vec3,
} from "../types";

/**
 * The four bucketed lists the renderer consumes. Each list is in the
 * order the spatial index produced them (no DRAWORDER guarantee here
 * — that lives downstream in `BatchedLayer.bulkLoad`).
 */
export interface VisibleEntityGroups {
  /** Entities with bucket "full" — feed into BatchedLayer. */
  full: RenderableEntity[];
  /** Entities with bucket "bbox-line" — render as four line segments. */
  bboxLine: RenderableEntity[];
  /** Entities with bucket "point" — render as one point each. */
  point: RenderableEntity[];
  /** Entities classified "skip" — exposed for diagnostics, not rendered. */
  skip: RenderableEntity[];
}

/**
 * One iteration of "what's visible this frame":
 *
 *   1. rbush spatial.intersect(viewport)  →  ids
 *   2. resolve each id to a full RenderableEntity via the index's
 *      constructor-supplied resolver (Opt 3)
 *   3. refresh LOD bucket per entity, applying hysteresis
 *   4. partition into the four buckets
 *
 * The `prevState` map is mutated in place — the caller is responsible
 * for retaining a stable reference across frames so hysteresis sees
 * the previous bucket.
 */
export function computeVisibleSet(args: {
  spatial: SpatialIndex;
  viewport: ViewportRect;
  cameraZoom: number;
  prevState: Map<string, LodEntityState>;
}): VisibleEntityGroups {
  const { spatial, viewport, cameraZoom, prevState } = args;
  const visibleIds = spatial.intersect(viewport);
  // Opt 3 — `intersect()` returns ids. Resolve through the index's
  // entity resolver; skip ids that no longer resolve (stale entries
  // between intersect and resolve are rare but possible during a
  // bulkLoad mid-frame).
  const visible: RenderableEntity[] = [];
  for (const id of visibleIds) {
    const entity = spatial.resolve(id);
    if (entity) visible.push(entity);
  }
  refreshLodState(prevState, visible, cameraZoom);

  const groups: VisibleEntityGroups = {
    full: [],
    bboxLine: [],
    point: [],
    skip: [],
  };
  for (const entity of visible) {
    const bucket = prevState.get(entity.id)?.bucket ?? "skip";
    pushIntoBucket(groups, bucket, entity);
  }
  return groups;
}

function pushIntoBucket(
  groups: VisibleEntityGroups,
  bucket: LodBucket,
  entity: RenderableEntity,
): void {
  switch (bucket) {
    case "full":
      groups.full.push(entity);
      return;
    case "bbox-line":
      groups.bboxLine.push(entity);
      return;
    case "point":
      groups.point.push(entity);
      return;
    case "skip":
      groups.skip.push(entity);
      return;
    default: {
      // Exhaustiveness guard: TS catches new unhandled buckets.
      const exhaustive: never = bucket;
      void exhaustive;
      return;
    }
  }
}

/**
 * Camera-change predicate: the visible set is recomputed when the
 * pan/zoom moves by more than `1` CSS pixel since the last
 * recomputation. Below that threshold the previous set is reused.
 *
 * `previous` is `null` on the very first frame — in which case we
 * always recompute.
 *
 * Pan thresholds are computed in *world units* by dividing 1 px by
 * the current zoom; zoom changes use a relative ratio (≥ 0.5 % delta).
 */
export interface CameraSnapshot {
  zoom: number;
  panX: number;
  panY: number;
}

export function shouldRecomputeVisibleSet(
  previous: CameraSnapshot | null,
  current: CameraSnapshot,
): boolean {
  if (previous === null) return true;
  // Zoom-relative change: ≥ 0.5 % triggers recompute. Avoids the
  // "moved 1 px" check from short-circuiting on a zoom-only frame
  // where pan distance is zero but the LOD buckets shifted wholesale.
  const zoomRatio = previous.zoom !== 0
    ? Math.abs(current.zoom - previous.zoom) / Math.abs(previous.zoom)
    : Number.POSITIVE_INFINITY;
  if (zoomRatio >= 0.005) return true;

  // Pan-by-1px threshold: convert 1 CSS px to world units via the
  // current zoom. `current.panX/Y` are already in world units (they
  // come from `SceneCamera.panX/Y`).
  const onePxWorld = current.zoom > 0 ? 1 / current.zoom : 0;
  const dx = Math.abs(current.panX - previous.panX);
  const dy = Math.abs(current.panY - previous.panY);
  return dx >= onePxWorld || dy >= onePxWorld;
}

/**
 * Build a `RenderableEntity` array from the bbox-line bucket: one
 * polyline per entity walking the four corners of its bbox. The
 * resulting entities feed the same line pipeline as PR-B (line
 * buckets in BatchedLayer), so no new shader path is needed.
 *
 * The bbox is closed (4 segments — top, right, bottom, left) and
 * carries the original entity id, layer, color, zOrder so selection
 * and layer-visibility wiring continues to work transparently.
 *
 * Z-coordinate is preserved from the original bounds when available
 * (currently always 0 for 2D space) so multi-Z entities (PR-L 3D
 * preview) won't degenerate.
 */
export function bboxLineRenderableFromEntity(
  entity: RenderableEntity,
): RenderableEntity {
  const { minX, minY, maxX, maxY } = entity.bounds;
  // Pull the entity's representative Z by sampling the geometry — we
  // don't store a min/maxZ on `bounds`. For 2D mode every Z is 0, so
  // this is just safety net.
  const z = sampleEntityZ(entity);
  const corners: Vec3[] = [
    { x: minX, y: minY, z },
    { x: maxX, y: minY, z },
    { x: maxX, y: maxY, z },
    { x: minX, y: maxY, z },
  ];
  return {
    ...entity,
    geometry: {
      kind: "polyline",
      points: corners,
      closed: true,
    },
  };
}

/**
 * Extract a representative Z from an entity geometry for the bbox-
 * line decimation. Returns the first Z we can find; defaults to 0.
 */
function sampleEntityZ(entity: RenderableEntity): number {
  const g = entity.geometry;
  switch (g.kind) {
    case "line":
      return g.a.z;
    case "polyline":
      return g.points[0]?.z ?? 0;
    case "mesh-wire":
      return g.runs[0]?.points[0]?.z ?? 0;
    case "circle":
    case "arc":
    case "ellipse":
      return g.center.z;
    case "spline":
      return g.controlPoints[0]?.z ?? 0;
    case "point":
      return g.position.z;
    case "text":
    case "mtext":
      return g.position.z;
    case "block-insert":
      return g.insertion.z;
    case "wipeout":
      return g.corners[0]?.z ?? 0;
    case "viewport":
      return g.rect[0]?.z ?? 0;
    case "hatch":
      return g.loops[0]?.vertices[0]?.z ?? 0;
    case "array-rect":
    case "dimension":
    case "multileader":
      return 0;
    default: {
      const exhaustive: never = g;
      void exhaustive;
      return 0;
    }
  }
}

/**
 * Center-of-bbox helper used by the points-cloud builder. Every
 * "point" bucket entity collapses to its bbox centre so the
 * THREE.Points cloud carries one position per entity.
 */
export function entityBboxCenter(entity: RenderableEntity): Vec3 {
  const cx = (entity.bounds.minX + entity.bounds.maxX) * 0.5;
  const cy = (entity.bounds.minY + entity.bounds.maxY) * 0.5;
  const cz = sampleEntityZ(entity);
  return { x: cx, y: cy, z: cz };
}
